import { inject, Injectable } from '@angular/core';
import { CertExtraTask, FeedbackCertification, Requirement } from '@app/core/models';
import { ModalManagerInterface } from '@app/core/services/modal-manager';
import { ModalServiceModel } from '@app/core/services/modal-manager/modal-service.model';
import {
  StaticValues,
  StaticValuesListService,
} from '@app/core/services/static-values-list/static-values-list.service';
import { DrupalConstants, ViewOptions, ViewService } from '@makiwin/ngx-drupal8-rest';
import { CertRequirementsComponent } from './cert-requirements.component';
import { UserPointsService } from '@app/core/services';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CertRequirementsService extends ModalServiceModel {
  private staticValuesListService = inject(StaticValuesListService);
  private userPointsService = inject(UserPointsService);
  private translateService = inject(TranslateService);

  private viewService = inject(ViewService);
  protected component = CertRequirementsComponent;
  cert?: FeedbackCertification;
  requirements?: Requirement[];
  extraTasks?: CertExtraTask[];
  // cert feedback
  feedbackId?: number;
  // eCourse quiz score
  highestScore = 0;
  isLoading = false;
  recOrATCOrITC = false;
  internshipHours = 0;

  open(modalManager: ModalManagerInterface, extraData: FeedbackCertification) {
    // Reset
    delete this.requirements;
    delete this.extraTasks;
    this.cert = extraData;
    this.isLoading = true;

    // WEB-4841: show btn only for rec, ATC and ITC certs
    const trainingLevelId = +extraData.training_levels[0]?.id;
    this.recOrATCOrITC =
      extraData.type === 'kiteboarder_certification' ||
      (extraData.type === 'instructor_certification' && (trainingLevelId === 5 || trainingLevelId === 2));

    const tasks = [];
    // Load requirements only for pro course
    if (extraData.type === 'instructor_certification') {
      tasks.push(this.getExtraTasks());
      tasks.push(this.getRequirements());
      tasks.push(this.getInternshipHours());
    }

    tasks.push(this.getFeedbacks());

    // Get eCourse scores and choose max
    if (extraData.type === 'ecourse_certification') {
      tasks.push(this.getQuizzesResults());
    }
    forkJoin(tasks).subscribe(() => {
      this.mapTasksAndInternship();
      this.isLoading = false;
    });
    return super.open(modalManager, extraData);
  }

  /**
   * Get user feedback for cert
   */
  private getFeedbacks() {
    if (!this.cert) {
      return of();
    }
    return this.viewService
      .get('/api/v1/user-feedbacks.json', {
        args: [this.cert.id],
        filters: { field_keep_it_private_value: 'All', field_moderation_state_value: 'All' },
      })
      .pipe(
        tap((data) => {
          this.feedbackId = data[0]?.cid;
          this.isLoading = false;
        })
      );
  }

  /**
   * Get current user quiz results
   */
  private getQuizzesResults() {
    if (!this.cert || !this.cert.course_id) {
      return of();
    }
    return this.viewService.get('/api/v1/course_bookings_by_nid.json', { args: [this.cert.course_id] }).pipe(
      tap((data) => {
        // Remove duplicates
        const results = [
          ...new Map(
            data
              .filter((b) => +b.uid === +DrupalConstants.Connection.current_user.uid)
              .map((item) => [item.result_id, item])
          ).values(),
        ];
        const bookingResultsScores = results.map((r) => r.score);
        if (bookingResultsScores.length > 0) {
          this.highestScore = Math.max(...bookingResultsScores);
        }
      })
    );
  }

  /**
   * Load extra tasks for pro cert
   */
  private getExtraTasks() {
    if (!this.cert) {
      return of();
    }
    return this.viewService
      .get('/api/v1/candidate-extra-tasks-for-pro-course.json', {
        args: [this.cert.course_id, DrupalConstants.Connection.current_user.uid],
      })
      .pipe(
        tap((data: CertExtraTask[]) => {
          this.extraTasks = data;
        })
      );
  }

  /**
   * Get level requirements for pro cert
   */
  private getRequirements() {
    if (!this.cert) {
      return of();
    }
    const viewOptions: ViewOptions = {
      filters: {
        op: 'getUserLevelProgress',
        level: this.cert?.training_levels[0]?.id,
        uid: DrupalConstants.Connection.current_user.uid,
        cert_id: this.cert?.id,
      },
    };
    return this.staticValuesListService.getValues(StaticValues.trainingLevels, viewOptions).pipe(
      tap((data: any) => {
        this.requirements = data.items;
      })
    );
  }

  private getInternshipHours() {
    return this.userPointsService.getQuantity('internship_hours', DrupalConstants.Connection.current_user.uid).pipe(
      tap((hours) => {
        this.internshipHours = hours;
      })
    );
  }

  private mapTasksAndInternship() {
    this.requirements?.forEach((r) => {
      switch (r.plugin_id) {
        case 'internship_hours_requirement':
          r.description = `${this.translateService.instant(
            'The candidate does not need to complete internship hours.'
          )} ${this.internshipHours} ${this.translateService.instant('Hours left.')}`;
          break;
        case 'completed_pro_notes_requirement':
          r.description = this.translateService.instant(
            'The candidate does not need to complete an extra task(s) to complete the course.'
          );
          break;
        case 'quiz_score_requirement':
          // Remove - English exam from the description, also works for other languages
          const descriptionFrags = r.description.split(' - ');
          const afterLangFrags = descriptionFrags[1].split(' ');
          afterLangFrags.shift();
          r.description = `${descriptionFrags[0]} ${afterLangFrags.join(' ').replace('exam ', '')}`;
          break;

        default:
          break;
      }
    });
  }
}
